import type { FulfillmentRegionsSettings } from "@redotech/redo-fulfillment-common/fulfillment-regions/settings";
import { DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT } from "@redotech/redo-order-core-common/order";
import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { defaultCompensationMethods } from "@redotech/redo-return-core-common/dropoff-provider";
import {
  DiscountDistributionMethod,
  ExcessExchangeValueOption,
  excessExchangeValueOptions,
  ExchangeFinancialStrategy,
  NonInstantExchangeOrderProvision,
  OutOfStockAdvanced,
} from "@redotech/redo-return-core-common/exchange-settings";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  ShopAgainAction,
  shopAgainActions,
} from "@redotech/redo-return-core-common/shop-again-action";
import {
  BLUE_YONDER_PROVIDER,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { ShopifyExchangeOrderStrategy } from "@redotech/redo-shopify-common/shopify";
import { isDefined } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { mergeDropoffAutoProcessMethods } from "./dropoff-settings-merge";
import {
  changedTeamPatchPaths,
  groupedSettingsChanges,
  sameSettingsValue,
} from "./grouped-settings-changes";
import { MerchantCreatedReturnLabelChargeDefault } from "./merchant-created-return-label-charge-default";
import { RefundDutiesMode } from "./refund-duties-mode";
import {
  dropoffSettingsFromStored,
  type StoredDropoff,
} from "./returns-locations-settings";

export enum ExchangeCreateTrigger {
  CREATION = "creation",
  CREATION_UPSELLS = "creation_upsells",
  RETURN_APPROVED = "return_approved",
  EXCHANGE_PRODUCTS_PROCESSED = "exchange_products_processed",
  ALL_PRODUCTS_PROCESSED = "all_products_processed",
}

export const RESTOCK_POLICIES = ["none", "not_damaged", "all"] as const;
export type RestockPolicy = (typeof RESTOCK_POLICIES)[number];

export const CLOSE_SHOPIFY_RETURN_OPTIONS = [
  "enabled",
  "greenReturnsOnly",
  "disabled",
] as const;
export type CloseShopifyReturnOption =
  (typeof CLOSE_SHOPIFY_RETURN_OPTIONS)[number];

export const STORE_CREDIT_EXPIRATION_MIN_DAYS = 1;
export const STORE_CREDIT_EXPIRATION_MAX_DAYS = 3650;

const STORE_CREDIT_PERMISSION = "credit";
export const DEFAULT_FEE_LINE_ITEM_NAME = "Return Fee";

export type DuplicateOrderNumberFormatError =
  | "missing_placeholder"
  | "too_long"
  | "numeric_prefix";

export function duplicateOrderNumberFormatError(
  value: string,
): DuplicateOrderNumberFormatError | undefined {
  if (!value.includes("{n}")) {
    return "missing_placeholder";
  }
  if (value.length > 10) {
    return "too_long";
  }
  const charBefore = value[value.indexOf("{n}") - 1];
  if (!charBefore || /\d/.test(charBefore)) {
    return "numeric_prefix";
  }
  return undefined;
}

export const ReturnsOperationsSettingsSchema = z.object({
  pointOfSale: z.object({
    restockByDefault: z.boolean(),
    sendOnlyCompletionEmail: z.boolean(),
    allowAdjustments: z.boolean(),
    allowManualDiscounts: z.boolean(),
  }),
  merchantCreatedReturns: z.object({
    enableMerchantReturnFlow: z.boolean(),
    labelChargeDefault: createEnum(MerchantCreatedReturnLabelChargeDefault),
    claimsRequireShipping: z.boolean(),
  }),
  /** Requires MANAGE_TEAM_ROLES; omitted when the caller lacks it. */
  staffPermissions: z
    .object({
      allowRefund: z.boolean(),
      allowStoreCredit: z.boolean(),
      allowExchange: z.boolean(),
      allowEditFeesAndBonuses: z.boolean(),
      allowEditGreenReturns: z.boolean(),
      allowEditReturn: z.boolean(),
    })
    .optional(),
  inventory: z.object({
    restock: z.enum(RESTOCK_POLICIES),
    restockShopifyRefunds: z.boolean(),
    skipEcommerceProviderRestock: z.boolean(),
    disposeShopifyReverseFulfillmentOnRestock: z.boolean(),
    allowRestockBeforeProcess: z.boolean(),
  }),
  shopify: z.object({
    createShopifyReturns: z.boolean(),
    createShopifyReturnTypes: z.array(createEnum(CompensationMethod)),
    createShopifyReturnsForClaimsAndWarranties: z.boolean(),
    createSeparateShopifyReturnForGreenReturns: z.boolean(),
    createMultipleShopifyReturns: z.boolean(),
    addTrackingToShopifyReturns: z.boolean(),
    archiveShopifyOrdersAfterReturn: z.boolean(),
    closeShopifyReturnWhenProcessed: z.enum(CLOSE_SHOPIFY_RETURN_OPTIONS),
    disableRefundLineItems: z.boolean(),
    shouldAutoProcessRedoReturnWhenShopifyReturnCloses: z.boolean(),
    applyRestockingFeeToShopifyReturns: z.boolean(),
    /** Requires SET_SHOPIFY_RETURN_FEE_SYNC_SETTINGS. */
    syncReturnFeesToShopify: z.boolean(),
    refundAllReturnedItems: z.boolean(),
    skipExchangeRefunds: z.boolean(),
    showStoreCreditOnRefundNote: z.boolean(),
    refundDutiesMode: createEnum(RefundDutiesMode),
    dutyDrawbackEnabled: z.boolean(),
    dutyDrawbackEligibleCountries: z.array(z.string()),
    addOrderNotes: z.boolean(),
  }),
  /** Stored on `team.automation`, outside `settings`. */
  storeCreditExpiration: z.object({
    enabled: z.boolean(),
    days: z
      .number()
      .int()
      .min(STORE_CREDIT_EXPIRATION_MIN_DAYS)
      .max(STORE_CREDIT_EXPIRATION_MAX_DAYS),
  }),
  general: z.object({
    waiveRefundStoreCreditBelowAmount: z.number().min(0),
    addReturnTagToOriginalOrder: z.boolean(),
    defaultReimburseOriginalShipping: z.boolean(),
    exposeExternalId: z.boolean(),
  }),
  orders: z.object({
    reuseOrderName: z.boolean(),
    shouldStripHash: z.boolean(),
    exchangeShippingName: z.string(),
    prefix: z.string(),
    suffix: z.string(),
    duplicateOrderNumberFormat: z
      .string()
      .refine((value) => duplicateOrderNumberFormatError(value) === undefined, {
        message: "Invalid duplicate order number format",
      }),
    exchangeFinancialStrategy: createEnum(ExchangeFinancialStrategy),
    addTaxToExchangeOrders: z.boolean(),
    createTrigger: createEnum(ExchangeCreateTrigger),
    /** Null when the team has no Shopify integration. */
    shopifyExchangeOrderStrategy: createEnum(
      ShopifyExchangeOrderStrategy,
    ).nullable(),
    addAdditionalExchangeTags: z.boolean(),
    shouldRemoveHoldTagWhenRelease: z.boolean(),
    separateQuantityForExchangeProducts: z.boolean(),
    applyFeesAsLineItem: z.boolean(),
    applyFeesAsLineItemName: z.string(),
  }),
  orderNotes: z.object({
    disabled: z.boolean(),
    includeDate: z.boolean(),
    includeExistingNote: z.boolean(),
    addNoteToOriginalOrders: z.boolean(),
  }),
  fulfillmentRegions: z.object({
    enabled: z.boolean(),
    regionMappings: z.array(
      z.object({ country: z.string(), locationIds: z.array(z.string()) }),
    ),
    defaultLocationIds: z.array(z.string()),
  }),
  fulfillment: z.object({
    excessExchangeValue: z.enum(excessExchangeValueOptions),
    outOfStockAdvanced: createEnum(OutOfStockAdvanced),
  }),
  processing: z.object({
    compensationEnabled: z.boolean(),
    restockingEnabled: z.boolean(),
    reviewAfterWarehouseProcessing: z.boolean(),
    checkForTryNowTags: z.boolean(),
  }),
  greenReturns: z.object({
    autoProcessGreenReturnTypes: z.array(createEnum(ReturnTypeEnum)),
    autoProcessMerchantCreatedGreenReturnTypes: z.array(
      createEnum(ReturnTypeEnum),
    ),
  }),
  /** Keyed by drop-off provider; shares `returns.dropoffs` with the Locations tab. */
  dropoffAutoProcessing: z.object({
    [VIRTUAL_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [RETURN_BEAR_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [BLUE_YONDER_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [HAPPY_RETURNS_PROVIDER]: z.array(createEnum(CompensationMethod)),
  }),
  predefinedCategories: z.object({
    enabled: z.boolean(),
    categories: z.array(z.object({ name: z.string(), code: z.string() })),
  }),
  /** Requires SET_SHIPPING_SETTINGS. */
  shipping: z.object({
    pickupMaxCoveredAmountEnabled: z.boolean(),
    pickupMaxCoveredAmount: z.number().min(0).nullable(),
    signatureEnabled: z.boolean(),
    /** USD regardless of shop currency. */
    signatureThreshold: z.number().min(0).nullable(),
    signatureForTagsEnabled: z.boolean(),
    signatureForTagsTags: z.array(z.string()),
  }),
  storeCredit: z.object({
    type: z.enum(shopAgainActions),
    riseTriggerUrl: z.string(),
    giftCardOrderProductId: z.string(),
    hideGiftCardBranding: z.boolean(),
  }),
});

export type ReturnsOperationsSettings = z.infer<
  typeof ReturnsOperationsSettingsSchema
>;

const settingsShape = ReturnsOperationsSettingsSchema.shape;

/** Only the fields a merchant changed; everything omitted is left as stored. */
export const ReturnsOperationsSettingsChangesSchema = z.object({
  pointOfSale: settingsShape.pointOfSale.partial().optional(),
  merchantCreatedReturns: settingsShape.merchantCreatedReturns
    .partial()
    .optional(),
  staffPermissions: settingsShape.staffPermissions
    .unwrap()
    .partial()
    .optional(),
  inventory: settingsShape.inventory.partial().optional(),
  shopify: settingsShape.shopify.partial().optional(),
  storeCreditExpiration: settingsShape.storeCreditExpiration
    .partial()
    .optional(),
  general: settingsShape.general.partial().optional(),
  orders: settingsShape.orders.partial().optional(),
  orderNotes: settingsShape.orderNotes.partial().optional(),
  fulfillmentRegions: settingsShape.fulfillmentRegions.partial().optional(),
  fulfillment: settingsShape.fulfillment.partial().optional(),
  processing: settingsShape.processing.partial().optional(),
  greenReturns: settingsShape.greenReturns.partial().optional(),
  dropoffAutoProcessing: settingsShape.dropoffAutoProcessing
    .partial()
    .optional(),
  predefinedCategories: settingsShape.predefinedCategories.partial().optional(),
  shipping: settingsShape.shipping.partial().optional(),
  storeCredit: settingsShape.storeCredit.partial().optional(),
});

export type ReturnsOperationsSettingsChanges = z.infer<
  typeof ReturnsOperationsSettingsChangesSchema
>;

export function returnsOperationsSettingsChanges(
  initial: ReturnsOperationsSettings,
  next: ReturnsOperationsSettings,
): ReturnsOperationsSettingsChanges {
  return ReturnsOperationsSettingsChangesSchema.parse(
    groupedSettingsChanges(initial, next),
  );
}

/** Not re-validated: a legacy stored value that fails today's schema must not block saving an unrelated field. */
function applyReturnsOperationsSettingsChanges(
  base: ReturnsOperationsSettings,
  changes: ReturnsOperationsSettingsChanges,
): ReturnsOperationsSettings {
  return {
    pointOfSale: { ...base.pointOfSale, ...changes.pointOfSale },
    merchantCreatedReturns: {
      ...base.merchantCreatedReturns,
      ...changes.merchantCreatedReturns,
    },
    staffPermissions: base.staffPermissions && {
      ...base.staffPermissions,
      ...changes.staffPermissions,
    },
    inventory: { ...base.inventory, ...changes.inventory },
    shopify: { ...base.shopify, ...changes.shopify },
    storeCreditExpiration: {
      ...base.storeCreditExpiration,
      ...changes.storeCreditExpiration,
    },
    general: { ...base.general, ...changes.general },
    orders: { ...base.orders, ...changes.orders },
    orderNotes: { ...base.orderNotes, ...changes.orderNotes },
    fulfillmentRegions: {
      ...base.fulfillmentRegions,
      ...changes.fulfillmentRegions,
    },
    fulfillment: { ...base.fulfillment, ...changes.fulfillment },
    processing: { ...base.processing, ...changes.processing },
    greenReturns: { ...base.greenReturns, ...changes.greenReturns },
    dropoffAutoProcessing: {
      ...base.dropoffAutoProcessing,
      ...changes.dropoffAutoProcessing,
    },
    predefinedCategories: {
      ...base.predefinedCategories,
      ...changes.predefinedCategories,
    },
    shipping: { ...base.shipping, ...changes.shipping },
    storeCredit: { ...base.storeCredit, ...changes.storeCredit },
  };
}

/** The slice of team settings the Operations tab reads. Structurally matches `Team["settings"]`. */
export interface ReturnsOperationsSettingsSource {
  settings: {
    pos?: {
      defaultPosRestock?: boolean | null;
      sendOnlyCompletionEmail?: boolean | null;
      allowPOSAdjustments?: boolean | null;
      allowPOSManualDiscounts?: boolean | null;
    } | null;
    inventory: {
      restock: boolean;
      restockDamaged: boolean;
      restockShopifyRefunds?: boolean | null;
      skipEcommerceProviderRestock?: boolean | null;
      disposeShopifyReverseFulfillmentOnRestock?: boolean | null;
      allowRestockBeforeProcess?: boolean | null;
    };
    exchanges: {
      suffix?: string | null;
      shouldStripHash: boolean;
      orderName: {
        prefix?: string | null;
        suffix?: string | null;
        useShopifyGeneratedName: boolean;
        duplicateOrderNumberFormat?: string | null;
      };
      enableOnHoldOrders: boolean;
      onHoldOrdersOnUpsellOnly?: boolean | null;
      nonInstantExchangeOrderProvision: NonInstantExchangeOrderProvision;
      exchangeShippingName?: string | null;
      exchangeFinancialStrategy?: ExchangeFinancialStrategy | null;
      addTaxToExchangeOrders?: boolean | null;
      excessExchangeValue: ExcessExchangeValueOption;
      outOfStockAdvanced: OutOfStockAdvanced;
      addAdditionalExchangeTags?: boolean | null;
      shouldRemoveHoldTagWhenRelease?: boolean | null;
      separateQuantityForExchangeProducts?: boolean | null;
      applyFeesAsLineItem?: boolean | null;
      applyFeesAsLineItemName?: string | null;
      notes?: {
        disabled?: boolean | null;
        includeDate?: boolean | null;
        includeExistingNote?: boolean | null;
        addNoteToOriginalOrders?: boolean | null;
      } | null;
      fulfillmentRegions?: FulfillmentRegionsSettings | null;
    };
    returns?: {
      permissions?: {
        allowedCompensationMethods?: readonly string[] | null;
        allowEditReturn?: boolean | null;
        allowEditFeesAndBonuses?: boolean | null;
        allowEditGreenReturns?: boolean | null;
      } | null;
      advancedProcessing?: {
        compensationEnabled?: boolean | null;
        restockingEnabled?: boolean | null;
      } | null;
      enableMerchantReturnFlow?: boolean | null;
      merchantCreatedReturnLabelChargeDefault?: MerchantCreatedReturnLabelChargeDefault | null;
      merchantCreatedClaimsRequireShipping?: boolean | null;
      hideGiftCardBranding?: boolean | null;
      shouldAutoProcessRedoReturnWhenShopifyReturnCloses?: boolean | null;
      applyRestockingFeeToShopifyReturns?: boolean | null;
      syncReturnFeesToShopify?: boolean | null;
      refundAllReturnedItems?: boolean | null;
      skipExchangeRefunds?: boolean | null;
      showStoreCreditOnRefundNote?: boolean | null;
      refundDutiesMode?: RefundDutiesMode | null;
      dutyDrawback?: {
        enabled?: boolean | null;
        eligibleCountries?: string[] | null;
      } | null;
      waiveRefundStoreCreditBelowAmount?: number | null;
      addReturnTagToOriginalOrder?: boolean | null;
      defaultReimburseOriginalShipping?: boolean | null;
      reviewAfterWarehouseProcessing?: boolean | null;
      checkForTryNowTags?: boolean | null;
      aiReturnReasons?: {
        usePredefinedCategories?: boolean | null;
        predefinedCategories?: { name: string; code: string }[] | null;
      } | null;
      requireReturnShippingSignature?: {
        enabled?: boolean | null;
        threshold?: number | null;
      } | null;
      requireReturnShippingSignatureForTags?: {
        enabled?: boolean | null;
        tags?: string[] | null;
      } | null;
      dropoffs?: StoredDropoff[] | null;
    } | null;
    autoProcessGreenReturnTypes?: ReturnTypeEnum[] | null;
    /** Deprecated boolean the array falls back to when absent. */
    autoProcessGreenReturns?: boolean | null;
    autoProcessMerchantCreatedGreenReturnTypes?: ReturnTypeEnum[] | null;
    pickupMaxCoveredAmountEnabled?: boolean | null;
    pickupMaxCoveredAmount?: number | null;
    returnsUI?: { exposeExternalId?: boolean | null } | null;
    createShopifyReturns?: boolean;
    createShopifyReturnTypes?: CompensationMethod[] | null;
    createShopifyReturnsForClaimsAndWarranties?: boolean;
    createSeparateShopifyReturnForGreenReturns?: boolean;
    createMultipleShopifyReturns?: boolean;
    addTrackingToShopifyReturns?: boolean;
    archiveShopifyOrdersAfterReturn?: boolean | null;
    closeShopifyReturnWhenProcessed?: CloseShopifyReturnOption | null;
    disableRefundLineItems?: boolean | null;
    addOrderNotes?: boolean | null;
    discountDistributionMethod?: DiscountDistributionMethod | null;
    shopAgainAction?: ShopAgainAction;
    riseTrigger?: { url: string } | null;
    giftCardOrderProductId?: string | null;
  };
  automation?: {
    storeCreditExpiration?: {
      enabled?: boolean | null;
      days?: number | null;
    } | null;
  } | null;
  shopifyExchangeOrderStrategy?: ShopifyExchangeOrderStrategy | null;
}

export function exchangeCreateTriggerFromSettings(exchanges: {
  enableOnHoldOrders: boolean;
  onHoldOrdersOnUpsellOnly?: boolean | null;
  nonInstantExchangeOrderProvision: NonInstantExchangeOrderProvision;
}): ExchangeCreateTrigger {
  if (exchanges.enableOnHoldOrders) {
    return exchanges.onHoldOrdersOnUpsellOnly
      ? ExchangeCreateTrigger.CREATION_UPSELLS
      : ExchangeCreateTrigger.CREATION;
  }
  switch (exchanges.nonInstantExchangeOrderProvision) {
    case NonInstantExchangeOrderProvision.ALL_PRODUCTS_PROCESSED:
      return ExchangeCreateTrigger.ALL_PRODUCTS_PROCESSED;
    case NonInstantExchangeOrderProvision.RETURN_APPROVED:
      return ExchangeCreateTrigger.RETURN_APPROVED;
    case NonInstantExchangeOrderProvision.EXCHANGE_PRODUCTS_PROCESSED:
      return ExchangeCreateTrigger.EXCHANGE_PRODUCTS_PROCESSED;
  }
}

function nonInstantProvisionFromTrigger(
  trigger: ExchangeCreateTrigger,
): NonInstantExchangeOrderProvision {
  switch (trigger) {
    case ExchangeCreateTrigger.EXCHANGE_PRODUCTS_PROCESSED:
      return NonInstantExchangeOrderProvision.EXCHANGE_PRODUCTS_PROCESSED;
    case ExchangeCreateTrigger.RETURN_APPROVED:
      return NonInstantExchangeOrderProvision.RETURN_APPROVED;
    case ExchangeCreateTrigger.CREATION:
    case ExchangeCreateTrigger.CREATION_UPSELLS:
    case ExchangeCreateTrigger.ALL_PRODUCTS_PROCESSED:
      return NonInstantExchangeOrderProvision.ALL_PRODUCTS_PROCESSED;
  }
}

/** A stored entry without the array reads as none; no entry at all reads as every method. */
function dropoffAutoProcessingFromStored(
  stored: StoredDropoff[] | null | undefined,
): ReturnsOperationsSettings["dropoffAutoProcessing"] {
  const methodsFor = (provider: (typeof dropoffProviderType)[number]) => {
    const existing = stored?.find((dropoff) => dropoff.provider === provider);
    return existing
      ? (existing.eligibleAutoProcessCompensationMethods ?? [])
      : defaultCompensationMethods(provider);
  };
  return {
    [VIRTUAL_PROVIDER]: methodsFor(VIRTUAL_PROVIDER),
    [RETURN_BEAR_PROVIDER]: methodsFor(RETURN_BEAR_PROVIDER),
    [BLUE_YONDER_PROVIDER]: methodsFor(BLUE_YONDER_PROVIDER),
    [HAPPY_RETURNS_PROVIDER]: methodsFor(HAPPY_RETURNS_PROVIDER),
  };
}

export function returnsOperationsSettingsFromTeam({
  settings,
  automation,
  shopifyExchangeOrderStrategy,
}: ReturnsOperationsSettingsSource): ReturnsOperationsSettings {
  const { exchanges, returns, pos } = settings;
  const permissions = returns?.permissions;
  const allowedMethods = permissions?.allowedCompensationMethods;
  const compensationEnabled =
    returns?.advancedProcessing?.compensationEnabled ?? false;
  return {
    pointOfSale: {
      restockByDefault: pos?.defaultPosRestock ?? true,
      sendOnlyCompletionEmail: pos?.sendOnlyCompletionEmail ?? true,
      allowAdjustments: pos?.allowPOSAdjustments ?? true,
      allowManualDiscounts: pos?.allowPOSManualDiscounts ?? true,
    },
    merchantCreatedReturns: {
      enableMerchantReturnFlow: returns?.enableMerchantReturnFlow ?? false,
      labelChargeDefault:
        returns?.merchantCreatedReturnLabelChargeDefault ??
        MerchantCreatedReturnLabelChargeDefault.AUTO,
      claimsRequireShipping:
        returns?.merchantCreatedClaimsRequireShipping ?? false,
    },
    staffPermissions: {
      allowRefund: allowedMethods?.includes(CompensationMethod.REFUND) ?? true,
      allowStoreCredit:
        allowedMethods?.includes(STORE_CREDIT_PERMISSION) ?? true,
      allowExchange:
        allowedMethods?.includes(CompensationMethod.EXCHANGE) ?? true,
      allowEditFeesAndBonuses: permissions?.allowEditFeesAndBonuses ?? true,
      allowEditGreenReturns: permissions?.allowEditGreenReturns ?? true,
      allowEditReturn: permissions?.allowEditReturn ?? true,
    },
    inventory: {
      restock: !settings.inventory.restock
        ? "none"
        : settings.inventory.restockDamaged
          ? "all"
          : "not_damaged",
      restockShopifyRefunds: settings.inventory.restockShopifyRefunds ?? false,
      skipEcommerceProviderRestock:
        settings.inventory.skipEcommerceProviderRestock ?? false,
      disposeShopifyReverseFulfillmentOnRestock:
        settings.inventory.disposeShopifyReverseFulfillmentOnRestock ?? false,
      allowRestockBeforeProcess:
        settings.inventory.allowRestockBeforeProcess ?? false,
    },
    shopify: {
      createShopifyReturns: !!settings.createShopifyReturns,
      createShopifyReturnTypes: settings.createShopifyReturnTypes ?? [],
      createShopifyReturnsForClaimsAndWarranties:
        !!settings.createShopifyReturnsForClaimsAndWarranties,
      createSeparateShopifyReturnForGreenReturns:
        !!settings.createSeparateShopifyReturnForGreenReturns,
      createMultipleShopifyReturns: !!settings.createMultipleShopifyReturns,
      addTrackingToShopifyReturns: settings.addTrackingToShopifyReturns ?? true,
      // The runtime treats an absent value as on.
      archiveShopifyOrdersAfterReturn:
        settings.archiveShopifyOrdersAfterReturn ?? true,
      closeShopifyReturnWhenProcessed:
        settings.closeShopifyReturnWhenProcessed ?? "enabled",
      disableRefundLineItems: settings.disableRefundLineItems ?? false,
      shouldAutoProcessRedoReturnWhenShopifyReturnCloses:
        returns?.shouldAutoProcessRedoReturnWhenShopifyReturnCloses ?? false,
      applyRestockingFeeToShopifyReturns:
        returns?.applyRestockingFeeToShopifyReturns ?? false,
      syncReturnFeesToShopify: returns?.syncReturnFeesToShopify ?? false,
      refundAllReturnedItems: returns?.refundAllReturnedItems ?? false,
      skipExchangeRefunds: returns?.skipExchangeRefunds ?? false,
      showStoreCreditOnRefundNote:
        returns?.showStoreCreditOnRefundNote ?? false,
      refundDutiesMode: returns?.refundDutiesMode ?? RefundDutiesMode.OFF,
      dutyDrawbackEnabled: returns?.dutyDrawback?.enabled ?? false,
      dutyDrawbackEligibleCountries:
        returns?.dutyDrawback?.eligibleCountries ?? [],
      addOrderNotes: settings.addOrderNotes ?? false,
    },
    storeCreditExpiration: {
      enabled: automation?.storeCreditExpiration?.enabled ?? false,
      days: automation?.storeCreditExpiration?.days || 60,
    },
    general: {
      waiveRefundStoreCreditBelowAmount:
        returns?.waiveRefundStoreCreditBelowAmount ?? 0,
      addReturnTagToOriginalOrder:
        returns?.addReturnTagToOriginalOrder ?? false,
      defaultReimburseOriginalShipping:
        returns?.defaultReimburseOriginalShipping ?? false,
      exposeExternalId: settings.returnsUI?.exposeExternalId ?? false,
    },
    orders: {
      reuseOrderName: !exchanges.orderName.useShopifyGeneratedName,
      shouldStripHash: exchanges.shouldStripHash,
      exchangeShippingName: exchanges.exchangeShippingName ?? "",
      prefix: exchanges.orderName.prefix ?? "",
      suffix: isDefined(exchanges.suffix)
        ? `-${exchanges.suffix}`
        : (exchanges.orderName.suffix ?? ""),
      duplicateOrderNumberFormat:
        exchanges.orderName.duplicateOrderNumberFormat ??
        DEFAULT_DUPLICATE_ORDER_NUMBER_FORMAT,
      exchangeFinancialStrategy:
        exchanges.exchangeFinancialStrategy ?? ExchangeFinancialStrategy.NONE,
      addTaxToExchangeOrders: exchanges.addTaxToExchangeOrders ?? false,
      createTrigger: exchangeCreateTriggerFromSettings(exchanges),
      shopifyExchangeOrderStrategy: shopifyExchangeOrderStrategy ?? null,
      addAdditionalExchangeTags: exchanges.addAdditionalExchangeTags ?? false,
      shouldRemoveHoldTagWhenRelease:
        exchanges.shouldRemoveHoldTagWhenRelease ?? false,
      separateQuantityForExchangeProducts:
        exchanges.separateQuantityForExchangeProducts ?? false,
      applyFeesAsLineItem: exchanges.applyFeesAsLineItem ?? false,
      applyFeesAsLineItemName:
        exchanges.applyFeesAsLineItemName ?? DEFAULT_FEE_LINE_ITEM_NAME,
    },
    orderNotes: {
      disabled: exchanges.notes?.disabled ?? false,
      includeDate: exchanges.notes?.includeDate ?? false,
      includeExistingNote: exchanges.notes?.includeExistingNote ?? true,
      addNoteToOriginalOrders:
        exchanges.notes?.addNoteToOriginalOrders ?? false,
    },
    fulfillmentRegions: {
      enabled: exchanges.fulfillmentRegions?.enabled ?? false,
      regionMappings: exchanges.fulfillmentRegions?.regionMappings ?? [],
      defaultLocationIds:
        exchanges.fulfillmentRegions?.defaultLocationIds ?? [],
    },
    fulfillment: {
      excessExchangeValue: exchanges.excessExchangeValue,
      outOfStockAdvanced: exchanges.outOfStockAdvanced,
    },
    processing: {
      compensationEnabled,
      restockingEnabled:
        compensationEnabled &&
        (returns?.advancedProcessing?.restockingEnabled ?? false),
      reviewAfterWarehouseProcessing:
        returns?.reviewAfterWarehouseProcessing ?? false,
      checkForTryNowTags: returns?.checkForTryNowTags ?? false,
    },
    greenReturns: {
      autoProcessGreenReturnTypes:
        settings.autoProcessGreenReturnTypes ??
        (settings.autoProcessGreenReturns ? Object.values(ReturnTypeEnum) : []),
      autoProcessMerchantCreatedGreenReturnTypes:
        settings.autoProcessMerchantCreatedGreenReturnTypes ?? [],
    },
    dropoffAutoProcessing: dropoffAutoProcessingFromStored(returns?.dropoffs),
    predefinedCategories: {
      enabled: returns?.aiReturnReasons?.usePredefinedCategories ?? false,
      categories:
        returns?.aiReturnReasons?.predefinedCategories?.map(
          ({ name, code }) => ({ name, code }),
        ) ?? [],
    },
    shipping: {
      pickupMaxCoveredAmountEnabled: !!settings.pickupMaxCoveredAmountEnabled,
      pickupMaxCoveredAmount: settings.pickupMaxCoveredAmount ?? null,
      signatureEnabled: !!returns?.requireReturnShippingSignature?.enabled,
      signatureThreshold:
        returns?.requireReturnShippingSignature?.threshold ?? null,
      signatureForTagsEnabled:
        !!returns?.requireReturnShippingSignatureForTags?.enabled,
      signatureForTagsTags:
        returns?.requireReturnShippingSignatureForTags?.tags ?? [],
    },
    storeCredit: {
      type: settings.shopAgainAction ?? "gift_card",
      riseTriggerUrl: settings.riseTrigger?.url ?? "",
      giftCardOrderProductId: settings.giftCardOrderProductId ?? "",
      hideGiftCardBranding: returns?.hideGiftCardBranding ?? false,
    },
  };
}

export interface ReturnsOperationsTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
  /** Absent when the Shopify integration should not be touched. */
  shopifyExchangeOrderStrategy?: ShopifyExchangeOrderStrategy;
  /** Top-level `team.automation.storeCreditExpiration`; absent when unchanged. */
  storeCreditExpiration?: { enabled: boolean; days: number };
}

export function returnsOperationsSettingsToTeamPatch(
  current: ReturnsOperationsSettingsSource,
  next: ReturnsOperationsSettings,
): ReturnsOperationsTeamPatch {
  const { settings } = current;
  const set: Record<string, unknown> = {
    "pos.defaultPosRestock": next.pointOfSale.restockByDefault,
    "pos.sendOnlyCompletionEmail": next.pointOfSale.sendOnlyCompletionEmail,
    "pos.allowPOSAdjustments": next.pointOfSale.allowAdjustments,
    "pos.allowPOSManualDiscounts": next.pointOfSale.allowManualDiscounts,
    "returns.enableMerchantReturnFlow":
      next.merchantCreatedReturns.enableMerchantReturnFlow,
    "returns.merchantCreatedReturnLabelChargeDefault":
      next.merchantCreatedReturns.labelChargeDefault,
    "returns.merchantCreatedClaimsRequireShipping":
      next.merchantCreatedReturns.claimsRequireShipping,
    "inventory.restock": next.inventory.restock !== "none",
    "inventory.restockDamaged": next.inventory.restock === "all",
    "inventory.restockShopifyRefunds": next.inventory.restockShopifyRefunds,
    "inventory.skipEcommerceProviderRestock":
      next.inventory.skipEcommerceProviderRestock,
    "inventory.disposeShopifyReverseFulfillmentOnRestock":
      next.inventory.disposeShopifyReverseFulfillmentOnRestock,
    "inventory.allowRestockBeforeProcess":
      next.inventory.allowRestockBeforeProcess,
    createShopifyReturns: next.shopify.createShopifyReturns,
    createShopifyReturnTypes: next.shopify.createShopifyReturnTypes,
    createShopifyReturnsForClaimsAndWarranties:
      next.shopify.createShopifyReturnsForClaimsAndWarranties,
    createSeparateShopifyReturnForGreenReturns:
      next.shopify.createSeparateShopifyReturnForGreenReturns,
    createMultipleShopifyReturns: next.shopify.createMultipleShopifyReturns,
    addTrackingToShopifyReturns: next.shopify.addTrackingToShopifyReturns,
    archiveShopifyOrdersAfterReturn:
      next.shopify.archiveShopifyOrdersAfterReturn,
    closeShopifyReturnWhenProcessed:
      next.shopify.closeShopifyReturnWhenProcessed,
    disableRefundLineItems: next.shopify.disableRefundLineItems,
    "returns.shouldAutoProcessRedoReturnWhenShopifyReturnCloses":
      next.shopify.shouldAutoProcessRedoReturnWhenShopifyReturnCloses,
    "returns.applyRestockingFeeToShopifyReturns":
      next.shopify.applyRestockingFeeToShopifyReturns,
    "returns.syncReturnFeesToShopify": next.shopify.syncReturnFeesToShopify,
    "returns.refundAllReturnedItems": next.shopify.refundAllReturnedItems,
    "returns.skipExchangeRefunds": next.shopify.skipExchangeRefunds,
    // Legacy stores false whenever refunds are not always created.
    "returns.showStoreCreditOnRefundNote":
      next.shopify.refundAllReturnedItems &&
      next.shopify.showStoreCreditOnRefundNote,
    "returns.refundDutiesMode": next.shopify.refundDutiesMode,
    "returns.dutyDrawback.enabled": next.shopify.dutyDrawbackEnabled,
    "returns.dutyDrawback.eligibleCountries":
      next.shopify.dutyDrawbackEligibleCountries,
    addOrderNotes: next.shopify.addOrderNotes,
    "returns.waiveRefundStoreCreditBelowAmount":
      next.general.waiveRefundStoreCreditBelowAmount,
    "returns.addReturnTagToOriginalOrder":
      next.general.addReturnTagToOriginalOrder,
    "returns.defaultReimburseOriginalShipping":
      next.general.defaultReimburseOriginalShipping,
    "exchanges.orderName.useShopifyGeneratedName": !next.orders.reuseOrderName,
    "exchanges.shouldStripHash": next.orders.shouldStripHash,
    "exchanges.orderName.prefix": next.orders.prefix,
    "exchanges.orderName.suffix": next.orders.suffix,
    "exchanges.orderName.duplicateOrderNumberFormat":
      next.orders.duplicateOrderNumberFormat,
    "exchanges.exchangeFinancialStrategy":
      next.orders.exchangeFinancialStrategy,
    "exchanges.addTaxToExchangeOrders": next.orders.addTaxToExchangeOrders,
    "exchanges.enableOnHoldOrders": [
      ExchangeCreateTrigger.CREATION,
      ExchangeCreateTrigger.CREATION_UPSELLS,
    ].includes(next.orders.createTrigger),
    "exchanges.nonInstantExchangeOrderProvision":
      nonInstantProvisionFromTrigger(next.orders.createTrigger),
    "exchanges.onHoldOrdersOnUpsellOnly":
      next.orders.createTrigger === ExchangeCreateTrigger.CREATION_UPSELLS,
    "exchanges.addAdditionalExchangeTags":
      next.orders.addAdditionalExchangeTags,
    "exchanges.shouldRemoveHoldTagWhenRelease":
      next.orders.shouldRemoveHoldTagWhenRelease,
    "exchanges.separateQuantityForExchangeProducts":
      next.orders.separateQuantityForExchangeProducts,
    "exchanges.applyFeesAsLineItem": next.orders.applyFeesAsLineItem,
    "exchanges.applyFeesAsLineItemName":
      next.orders.applyFeesAsLineItemName.trim() || DEFAULT_FEE_LINE_ITEM_NAME,
    "exchanges.notes.disabled": next.orderNotes.disabled,
    "exchanges.notes.includeDate": next.orderNotes.includeDate,
    "exchanges.notes.includeExistingNote": next.orderNotes.includeExistingNote,
    "exchanges.notes.addNoteToOriginalOrders":
      next.orderNotes.addNoteToOriginalOrders,
    // Written whole: every sub-field is required once the object exists.
    "exchanges.fulfillmentRegions": {
      enabled: next.fulfillmentRegions.enabled,
      regionMappings: next.fulfillmentRegions.regionMappings.filter(
        (mapping) => mapping.country && mapping.locationIds.length > 0,
      ),
      defaultLocationIds: next.fulfillmentRegions.defaultLocationIds,
    },
    "exchanges.excessExchangeValue": next.fulfillment.excessExchangeValue,
    "exchanges.outOfStockAdvanced": next.fulfillment.outOfStockAdvanced,
    "returns.advancedProcessing.compensationEnabled":
      next.processing.compensationEnabled,
    "returns.advancedProcessing.restockingEnabled":
      next.processing.compensationEnabled && next.processing.restockingEnabled,
    "returns.reviewAfterWarehouseProcessing":
      next.processing.reviewAfterWarehouseProcessing,
    "returns.checkForTryNowTags": next.processing.checkForTryNowTags,
    autoProcessGreenReturnTypes: next.greenReturns.autoProcessGreenReturnTypes,
    autoProcessMerchantCreatedGreenReturnTypes:
      next.greenReturns.autoProcessMerchantCreatedGreenReturnTypes,
    "returns.dropoffs": mergeDropoffAutoProcessMethods(
      settings.returns?.dropoffs?.map(dropoffSettingsFromStored),
      dropoffProviderType.map((provider) => ({
        provider,
        methods: next.dropoffAutoProcessing[provider],
      })),
    ),
    "returns.aiReturnReasons.usePredefinedCategories":
      next.predefinedCategories.enabled,
    // Incomplete rows are dropped, as the legacy page did on save.
    "returns.aiReturnReasons.predefinedCategories":
      next.predefinedCategories.categories
        .map(({ name, code }) => ({ name: name.trim(), code: code.trim() }))
        .filter(({ name, code }) => name && code),
    "returnsUI.exposeExternalId": next.general.exposeExternalId,
    pickupMaxCoveredAmountEnabled: next.shipping.pickupMaxCoveredAmountEnabled,
    "returns.requireReturnShippingSignature.enabled":
      next.shipping.signatureEnabled,
    "returns.requireReturnShippingSignatureForTags.enabled":
      next.shipping.signatureForTagsEnabled,
    "returns.requireReturnShippingSignatureForTags.tags":
      next.shipping.signatureForTagsTags,
    shopAgainAction: next.storeCredit.type,
    "returns.hideGiftCardBranding": next.storeCredit.hideGiftCardBranding,
  };
  const unset: string[] = [];

  if (isDefined(settings.exchanges.suffix)) {
    set["exchanges.suffix"] = null;
  }

  const shippingName = next.orders.exchangeShippingName.trim();
  if (shippingName) {
    set["exchanges.exchangeShippingName"] = shippingName;
  } else if (isDefined(settings.exchanges.exchangeShippingName)) {
    unset.push("exchanges.exchangeShippingName");
  }

  if (next.shipping.pickupMaxCoveredAmount !== null) {
    set.pickupMaxCoveredAmount = next.shipping.pickupMaxCoveredAmount;
  } else if (isDefined(settings.pickupMaxCoveredAmount)) {
    unset.push("pickupMaxCoveredAmount");
  }
  if (next.shipping.signatureThreshold !== null) {
    set["returns.requireReturnShippingSignature.threshold"] =
      next.shipping.signatureThreshold;
  } else if (
    isDefined(settings.returns?.requireReturnShippingSignature?.threshold)
  ) {
    unset.push("returns.requireReturnShippingSignature.threshold");
  }

  const riseTriggerUrl = next.storeCredit.riseTriggerUrl.trim();
  if (riseTriggerUrl) {
    set.riseTrigger = { url: riseTriggerUrl };
  } else if (isDefined(settings.riseTrigger)) {
    unset.push("riseTrigger");
  }

  const giftCardOrderProductId = next.storeCredit.giftCardOrderProductId.trim();
  if (giftCardOrderProductId) {
    set.giftCardOrderProductId = giftCardOrderProductId;
  } else if (isDefined(settings.giftCardOrderProductId)) {
    unset.push("giftCardOrderProductId");
  }

  if (next.staffPermissions) {
    const allowed: string[] = [];
    if (next.staffPermissions.allowRefund) {
      allowed.push(CompensationMethod.REFUND);
    }
    if (next.staffPermissions.allowStoreCredit) {
      allowed.push(STORE_CREDIT_PERMISSION);
    }
    if (next.staffPermissions.allowExchange) {
      allowed.push(CompensationMethod.EXCHANGE);
    }
    set["returns.permissions.allowedCompensationMethods"] = allowed;
    set["returns.permissions.allowEditFeesAndBonuses"] =
      next.staffPermissions.allowEditFeesAndBonuses;
    set["returns.permissions.allowEditGreenReturns"] =
      next.staffPermissions.allowEditGreenReturns;
    set["returns.permissions.allowEditReturn"] =
      next.staffPermissions.allowEditReturn;
  }

  const patch: ReturnsOperationsTeamPatch = {
    set,
    unset,
    storeCreditExpiration: {
      enabled: next.storeCreditExpiration.enabled,
      days: next.storeCreditExpiration.days,
    },
  };

  const turningShopifyReturnsOff =
    !!settings.createShopifyReturns && !next.shopify.createShopifyReturns;
  const requestedStrategy = turningShopifyReturnsOff
    ? ShopifyExchangeOrderStrategy.ORDER
    : next.orders.shopifyExchangeOrderStrategy;
  if (
    requestedStrategy !== null &&
    requestedStrategy !== current.shopifyExchangeOrderStrategy
  ) {
    patch.shopifyExchangeOrderStrategy = requestedStrategy;
    if (
      requestedStrategy === ShopifyExchangeOrderStrategy.RETURN &&
      settings.discountDistributionMethod === DiscountDistributionMethod.ORDER
    ) {
      set.discountDistributionMethod = DiscountDistributionMethod.LINE_ITEM;
    }
  }

  return patch;
}

/**
 * Writes only what the merchant changed. Untouched fields are rebuilt from the
 * stored settings, so a stale form can't revert edits made elsewhere, and a
 * change to one field still produces every legacy field its encoding spans.
 */
export function returnsOperationsTeamPatchForChanges(
  current: ReturnsOperationsSettingsSource,
  changes: ReturnsOperationsSettingsChanges,
): ReturnsOperationsTeamPatch {
  const storedValue = returnsOperationsSettingsFromTeam(current);
  const baseline = returnsOperationsSettingsToTeamPatch(current, storedValue);
  const requested = returnsOperationsSettingsToTeamPatch(
    current,
    applyReturnsOperationsSettingsChanges(storedValue, changes),
  );

  const { set, unset } = changedTeamPatchPaths(baseline, requested);
  if (
    "exchanges.orderName.suffix" in set &&
    "exchanges.suffix" in requested.set
  ) {
    set["exchanges.suffix"] = null;
  }
  const patch: ReturnsOperationsTeamPatch = { set, unset };
  if (
    requested.shopifyExchangeOrderStrategy !== undefined &&
    requested.shopifyExchangeOrderStrategy !==
      baseline.shopifyExchangeOrderStrategy
  ) {
    patch.shopifyExchangeOrderStrategy = requested.shopifyExchangeOrderStrategy;
  }
  if (
    !sameSettingsValue(
      requested.storeCreditExpiration,
      baseline.storeCreditExpiration,
    )
  ) {
    patch.storeCreditExpiration = requested.storeCreditExpiration;
  }
  return patch;
}

/** The Shipping-page rows kept their shipping permission on the Operations tab. */
export function returnsOperationsChangesNeedShippingPermission(
  changes: ReturnsOperationsSettingsChanges,
): boolean {
  return Object.keys(changes.shipping ?? {}).length > 0;
}

/** Fee sync carries its own permission; the rest of the tab uses the returns one. */
export function returnsOperationsChangesNeedFeeSyncPermission(
  changes: ReturnsOperationsSettingsChanges,
): boolean {
  return changes.shopify?.syncReturnFeesToShopify !== undefined;
}
